import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, finalize, map, of, shareReplay, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { FileAccessMode } from '../modules/shared/interfaces/files.interface';
import { FilesService } from './files.service';

export type FileAccessPurpose = 'inline' | 'download' | 'voice';

export interface FileAccessTarget {
  fileId: string;
  accessMode?: FileAccessMode;
  requestId?: string;
  legacyUrl?: string;
}

export interface FileAccessCapability {
  url: string;
  expiresAt: string;
}

interface CachedCapability extends FileAccessCapability {
  expiresAtMs: number;
}

@Injectable({ providedIn: 'root' })
export class FileAccessService implements OnDestroy {
  private static readonly CAPABILITY_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;
  private readonly capabilities = new Map<string, CachedCapability>();
  private readonly pending = new Map<string, Observable<string>>();
  private readonly objectUrls = new Map<string, Set<string>>();
  private readonly apiUrl = new URL(environment.apiUrl);
  private readonly capabilityOrigin = this.apiUrl.origin;
  private readonly capabilityPathPrefix = FileAccessService.capabilityPathPrefix(
    this.apiUrl.pathname
  );
  private cacheGeneration = 0;

  constructor(
    private readonly http: HttpClient,
    private readonly filesService: FilesService
  ) {}

  resolve(target: FileAccessTarget, purpose: FileAccessPurpose): Observable<string> {
    if (target.accessMode !== 'routing_capability') {
      return of(target.legacyUrl || this.filesService.getDownloadFileUrl(target.fileId));
    }

    if (!target.requestId) {
      return throwError(() => new Error('Routing file access requires a requestId'));
    }

    const key = this.cacheKey(target.requestId, target.fileId, purpose);
    const cached = this.capabilities.get(key);
    if (cached && cached.expiresAtMs > Date.now()) {
      return of(cached.url);
    }
    this.capabilities.delete(key);

    const inFlight = this.pending.get(key);
    if (inFlight) {
      return inFlight;
    }

    const cacheGeneration = this.cacheGeneration;
    const request$ = this.http
      .post<FileAccessCapability>(
        `${environment.apiUrl}/files/${encodeURIComponent(target.fileId)}/access-capability`,
        { requestId: target.requestId, purpose }
      )
      .pipe(
        map(capability => this.validateCapability(capability)),
        map(capability => {
          if (cacheGeneration === this.cacheGeneration) {
            this.capabilities.set(key, capability);
          }
          return capability.url;
        }),
        finalize(() => {
          if (this.pending.get(key) === request$) {
            this.pending.delete(key);
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.pending.set(key, request$);
    return request$;
  }

  trackObjectUrl(requestId: string, objectUrl: string): void {
    const urls = this.objectUrls.get(requestId) ?? new Set<string>();
    urls.add(objectUrl);
    this.objectUrls.set(requestId, urls);
  }

  releaseObjectUrl(requestId: string, objectUrl: string): void {
    URL.revokeObjectURL(objectUrl);
    const urls = this.objectUrls.get(requestId);
    urls?.delete(objectUrl);
    if (!urls?.size) {
      this.objectUrls.delete(requestId);
    }
  }

  clearRequest(requestId: string): void {
    this.cacheGeneration += 1;
    const prefix = `${requestId}:`;
    for (const key of this.capabilities.keys()) {
      if (key.startsWith(prefix)) {
        this.capabilities.delete(key);
      }
    }
    for (const key of this.pending.keys()) {
      if (key.startsWith(prefix)) {
        this.pending.delete(key);
      }
    }

    const urls = this.objectUrls.get(requestId);
    urls?.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.delete(requestId);
  }

  clearAll(): void {
    this.cacheGeneration += 1;
    this.capabilities.clear();
    this.pending.clear();
    for (const urls of this.objectUrls.values()) {
      urls.forEach(url => URL.revokeObjectURL(url));
    }
    this.objectUrls.clear();
  }

  ngOnDestroy(): void {
    this.clearAll();
  }

  private cacheKey(requestId: string, fileId: string, purpose: FileAccessPurpose): string {
    return `${requestId}:${fileId}:${purpose}`;
  }

  private validateCapability(capability: FileAccessCapability): CachedCapability {
    if (
      typeof capability?.url !== 'string' ||
      typeof capability?.expiresAt !== 'string'
    ) {
      throw new Error('Invalid file access capability');
    }

    const expiresAtMs = Date.parse(capability.expiresAt);
    let parsedUrl: URL;
    let decodedPathname: string;

    try {
      parsedUrl = new URL(capability.url);
      decodedPathname = decodeURIComponent(parsedUrl.pathname);
    } catch {
      throw new Error('File access capability URL must be absolute');
    }

    const capabilityToken = decodedPathname.startsWith(this.capabilityPathPrefix)
      ? decodedPathname.slice(this.capabilityPathPrefix.length)
      : '';

    if (
      !['http:', 'https:'].includes(parsedUrl.protocol) ||
      parsedUrl.origin !== this.capabilityOrigin ||
      parsedUrl.username ||
      parsedUrl.password ||
      capability.url.includes('?') ||
      capability.url.includes('#') ||
      parsedUrl.pathname.includes('%') ||
      !FileAccessService.CAPABILITY_TOKEN_PATTERN.test(capabilityToken) ||
      !Number.isFinite(expiresAtMs)
    ) {
      throw new Error('Invalid file access capability');
    }
    if (expiresAtMs <= Date.now()) {
      throw new Error('File access capability is already expired');
    }

    return { ...capability, expiresAtMs };
  }

  private static capabilityPathPrefix(apiPathname: string): string {
    let decodedPathname: string;
    try {
      decodedPathname = decodeURIComponent(apiPathname);
    } catch {
      throw new Error('Invalid API URL pathname');
    }

    const segments = decodedPathname.split('/').filter(Boolean);
    if (segments.some(segment => segment === '.' || segment === '..' || segment.includes('\\'))) {
      throw new Error('Invalid API URL pathname');
    }

    const basePath = segments.length ? `/${segments.join('/')}` : '';
    return `${basePath}/files/access/`;
  }
}
